import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useRepMode } from "./RepModeContext";
import { fetchRepCircle, logRepContact } from "./repServices";
import { EMPTY_VALUE, circleFilter, mailtoFor } from "../../ForCoaches/v2/coachUtils";
import { timeAgo } from "../../ForBrands/brandDealUtils";
import { Avatar, Badge, Btn, Card, Chip, ErrorState, Loading, PageHeader } from "../../ForBrands/ui/BrandUI";

// Rep Mode · My Circle (Figma 04 · My Circle): the athletes who linked this
// rep and the people around them (guardians, other agents), with minors who
// have no guardian on file flagged for a consent request. A contact
// network, not a deal thread: messages go through the rep's mail app and
// are logged so Last contact stays accurate.

const FILTERS = [
  { value: "all", label: "Everyone", key: "all" },
  { value: "athletes", label: "Athletes", key: "athletes" },
  { value: "guardians", label: "Guardians", key: "guardians" },
  { value: "agents", label: "Other agents", key: "agents" },
];

const initialsFor = (row) => {
  if (row.kind === "missing") return "??";
  const parts = (row.name || "").split(" ").filter(Boolean);
  return `${parts[0]?.[0] || ""}${parts[1]?.[0] || ""}`.toUpperCase() || "?";
};

const RepCirclePage = () => {
  const { openShare, me } = useRepMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const payload = await fetchRepCircle();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your Circle");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rows = useMemo(() => circleFilter(data?.contacts, filter), [data, filter]);

  const reach = async (row) => {
    setBusy(row.id);
    try {
      if (row.kind === "missing") {
        await logRepContact({ email: row.athleteEmail, name: row.linkedTo, kind: "request_link", athleteEmail: row.athleteEmail });
        window.location.href = mailtoFor({
          email: row.athleteEmail,
          subject: "Please add your parent or guardian to your CACHE Circle",
          body: "Hi,\n\nYour NIL deals need a parent or guardian to co-sign. Please open My Circle in CACHE and add them so we can move forward.\n\n",
        });
      } else {
        if (!row.email) {
          toast.error("No email on file for this contact.");
          return;
        }
        await logRepContact({ email: row.email, name: row.name, kind: "message", athleteEmail: row.athleteEmail });
        window.location.href = mailtoFor({ email: row.email, subject: `Checking in from ${me.firstName ? `${me.firstName} ${me.lastName}`.trim() : "your rep"}` });
      }
      await load();
    } catch (err) {
      toast.error(err.message || "Could not log the contact");
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const counts = data.counts || {};

  return (
    <div>
      <PageHeader
        eyebrow="Rep Mode"
        title="My Circle"
        subtitle="Reach your athletes and the people who support them: guardians, other agents and the brands working with them."
        actions={<Btn onClick={openShare}>Share your Rep ID</Btn>}
      />

      <div className="flex flex-col gap-4 pt-2">
        <Card padding="px-5 py-4" className="flex items-start gap-3">
          <Badge tone="outline" size="m">
            How it works
          </Badge>
          <p className="bm-body-m text-bm-muted">
            Athletes link you by entering your Rep ID{data.repCode ? ` (${data.repCode})` : ""} in My Circle. Message opens your mail app with the contact filled in, and every
            message, reminder and consent request is logged so Last contact stays accurate. Guardians come from each athlete's own Circle; athletes under 18
            without a guardian on file are flagged here.
          </p>
        </Card>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <Chip key={f.value} active={filter === f.value} count={counts[f.key]} onClick={() => setFilter(f.value)}>
              {f.label}
            </Chip>
          ))}
        </div>

        <Card padding="p-0" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-bm-subtle">
                  <th className="bm-th pl-4 text-left">Contact</th>
                  <th className="bm-th text-left">Role</th>
                  <th className="bm-th text-left">Linked to</th>
                  <th className="bm-th text-left">Last contact</th>
                  <th className="bm-th pr-4 text-right" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center bm-body-m text-bm-muted">
                      {data.contacts?.length ? "Nobody in this group yet." : "Share your Rep ID and the athletes who link you, with their guardians and agents, appear here."}
                    </td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-bm-border">
                    <td className="bm-td pl-4">
                      <span className="flex items-center gap-3">
                        <Avatar src={row.avatar} initials={initialsFor(row)} size={40} tone="outline" />
                        <span className="flex min-w-0 flex-col">
                          <span className="bm-h-xs text-bm-ink">{row.name}</span>
                          <span className="truncate bm-body-s text-bm-muted">{row.subtitle || row.email || EMPTY_VALUE}</span>
                        </span>
                      </span>
                    </td>
                    <td className="bm-td">
                      <span className="flex items-center gap-2">
                        <Badge tone="outline">{row.role}</Badge>
                        {row.kind === "athlete" && row.shareFinancials && <Badge tone="soft">Shares financial terms</Badge>}
                      </span>
                    </td>
                    <td className="bm-td bm-body-m text-bm-ink">{row.linkedTo}</td>
                    <td className={`bm-td bm-body-m ${row.lastContact ? "text-bm-ink" : "text-bm-tertiary"}`}>{row.lastContact ? timeAgo(row.lastContact) : row.kind === "missing" ? EMPTY_VALUE : "Never"}</td>
                    <td className="bm-td pr-4 text-right">
                      <Btn variant="secondary" size="sm" onClick={() => reach(row)} disabled={busy === row.id}>
                        {busy === row.id ? "Opening..." : row.action}
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RepCirclePage;
